import {FC, useCallback, useEffect, useState} from "react";
import {Breadcrumb, Button, Container, Nav, Table} from "react-bootstrap";
import {ConfirmModal, FormText, Pager, useLoader, useMessage} from "backoffice/ui/Components";
import {useNavigate} from "react-router";
import {useTRPC} from "backoffice/ui/App";
import {useSearchParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import {WithdrawTask} from "src/rds/types/crowdbank/t_withdraw_task.ts";
import {download} from "src/utils/react/download.tsx";
import Encoding from "encoding-japanese";
import {convert} from "backoffice/ui/Pages/Members/Withdraw/List/plazai.ts";

const pageSize = 50;

export const MembersWithdrawListPage: FC = () => {

  const Loader   = useLoader(),
        Message  = useMessage(),
        Navigate = useNavigate(),
        tRPC     = useTRPC();

  // ステート
  const [modal, setModal]   = useState<{ type: string, task: WithdrawTask } | false>(false),
        [date, setDate]     = useState(''),
        [query, setQuery]   = useSearchParams(),
        [result, setResult] = useState({total: 0, list: [] as WithdrawTask[]});


  const nextAction = query.get('nextAction') || '全銀協ファイル登録' as WithdrawTask['next_action'],
        page       = Number(query.get('page')) || 1;

  // 初期化
  useEffect(() => {
    Loader.task(() => tRPC.member.withdraw.list.query({nextAction, page, pageSize}), 300)
          .then(({total, list}) => setResult({total, list}))
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }, [query]);


  // ステータス変更
  const handleChangeNextAction = (nextAction: string) => {
    setModal(false);

    query.set('nextAction', nextAction);
    handlePage(1);
  };

  // ページ遷移
  const handlePage = (page: number) => {
    setModal(false);

    query.set('page', String(page));
    query.set('ts', String(new Date().getTime()));

    setResult({total: 0, list: []});
    setQuery(query);
  }


  // 全銀協ファイルダウンロード
  const downloadFile = async () => {
    if (modal === false) {
      return;
    }

    const {task} = modal;

    Loader.task(impl, 500)
          .then(url => location.href = url!)
          .then(() => handlePage(1))
          .catch(err => {
            console.error(err);
            Message.error(err);
          });

    // ポーリング間隔
    const INTERVAL = 2000;

    // 実行回数
    let count = 0;

    //  処理本体
    async function impl() {

      // ファイル作成要求
      const {key} = await tRPC.member.withdraw.createBankFile.mutate({...task, date});
      while (true) {
        await Date.sleep(INTERVAL);

        // ステータスチェック
        const {status, url} = await tRPC.member.withdraw.downloadBankFile.query({key});
        if (status === 'OK') {
          return url;
        }

        // no-itemが 10秒以上続いた場合、エラー
        else if (status === 'no-item' && count > (10_000 / INTERVAL)) {
          throw new Error('作成中にエラーが発生しました');
        }
        count++;
      }
    }
  }


  const register = () => {
    if (modal === false) {
      return;
    }
    const {task} = modal;

    Loader.task(() => tRPC.member.withdraw.register.mutate({...task}), 500)
          .then(() => Message.show('「総合振込」承認登録が完了しました', () => handlePage(1)))
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }


  // 承認
  const complete = () => {
    if (modal === false) {
      return;
    }

    const {task} = modal;

    Loader.task(impl, 500)
          .then(() => downloadPlazai(task.id))
          .then(() => Message.show('「顧客出金」承認登録が完了しました', () => handlePage(1)))
          .catch(err => {
            console.error(err);
            Message.error(err);
          });

    // ポーリング間隔
    const INTERVAL = 2000;

    // 実行回数
    let count = 0;

    //  処理本体
    async function impl() {

      // ファイル作成要求
      const {key} = await tRPC.member.withdraw.launchComplete.mutate({...task, date});
      while (true) {
        await Date.sleep(INTERVAL);

        // ステータスチェック
        const {status} = await tRPC.member.withdraw.checkComplete.query({key});
        if (status === 'OK') {
          return;
        }

        // no-itemが 10秒以上続いた場合、エラー
        else if (status === 'no-item' && count > (10_000 / INTERVAL)) {
          throw new Error('作成中にエラーが発生しました');
        }
        count++;
      }
    }
  }

  const downloadPlazai = useCallback((id: number) => {
    Loader.task(() => tRPC.member.withdraw.detail.query({id, strict: true}), 300)
          .then(({task, list, goldBuying}) => {

            const yyyymmdd   = new Date(task.data.process_date).yyyymmdd(),
                  source     = convert(task, list, goldBuying ?? 0),
                  sjisData   = Encoding.convert(source, {to: 'SJIS', from: 'UNICODE', type: 'arraybuffer'}),
                  uint8Array = new Uint8Array(sjisData),
                  blob       = new Blob([uint8Array], {type: 'text/csv;charset=shift-jis;'});

            download(`${yyyymmdd}振込_CB出金_CSJ_交換処理19.csv`, blob);

          })
          .catch(err => {
            console.error(err);
            Message.error(err);
          });
  }, []);

  return (
      <>
        <Helmet>
          <title>出金管理</title>
        </Helmet>

        <div className={'container'}>
          <Breadcrumb>
            <Breadcrumb.Item active>資金管理</Breadcrumb.Item>
            <Breadcrumb.Item active>出金管理</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        <Container>
          <Nav fill variant="tabs"
               activeKey={nextAction}
               onSelect={key => handleChangeNextAction(key || '信託銀行出金申請')}>


            <Nav.Item>
              <Nav.Link eventKey="全銀協ファイル登録">全銀協ファイル登録</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="出金確認">出金完了確認</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="完了">完了</Nav.Link>
            </Nav.Item>
          </Nav>

          <div className={'m-3 mt-5'}>
            <Table>
              <thead>
              <tr>
                <th className={'text-end   '}>ID</th>
                <th className={'text-end   '}>金額</th>
                <th className={'text-end   '}>申請件数</th>
                <th className={'text-end   '}>ユーザー数</th>
                <th className={'text-center'}>対象期間</th>
                <th className={'text-center'}>全銀協ファイル</th>
                <th className={'text-center'}>振込日</th>
                <th className={'text-center'}>顧客出金確認</th>
                <th className={'text-center'}>&nbsp;</th>
              </tr>
              </thead>
              <tbody>
              {result.list.length === 0 && (
                  <tr>
                    <td colSpan={9} className={'text-center'}>なし</td>
                  </tr>
              )}

              {result.list.map(task => (
                  <tr key={task.id}>
                    <td className={'align-middle text-end'}>{task.id}</td>
                    <td className={'align-middle text-end'}>
                      {task.amount.toString().withComma}<br/>
                      <span className={'text-danger'}>
                        不能 / {task.cancel_amount.toString().withComma}
                      </span>
                    </td>
                    <td className={'align-middle text-end'}>{task.application_count}</td>
                    <td className={'align-middle text-end'}>{task.user_count}</td>

                    <td className={'align-middle text-center'}>
                      {task.data.order_time?.min ?? '----/--/--'}&nbsp;~<br/>
                      {task.data.order_time?.max ?? '----/--/--'}
                    </td>
                    <td className={'align-middle text-center'}>
                      {task.downloaded_by?.name ?? '--'}<br/>
                      {task.downloaded_at ?? '----/--/--'}
                    </td>
                    <td className={'align-middle text-center'}>
                      {task.data.process_date ?? '----/--/--'}
                    </td>
                    <td className={'align-middle text-center'}>
                      {task.confirmed_by?.name ?? '--'}<br/>
                      {task.confirmed_at ?? '----/--/--'}
                    </td>
                    <td className={'align-middle text-center'}>
                      {nextAction === '全銀協ファイル登録' && (
                          <>
                            <Button className={'me-1'}
                                    variant={'outline-primary'}
                                    onClick={() => {
                                      setModal({type: 'download', task});
                                      setDate(new Date().addDate(1).toYmd());
                                    }}>
                              ダウンロード
                            </Button>

                            <Button className={'me-1'}
                                    variant={'outline-secondary'}
                                    onClick={() => {
                                      setModal({type: 'register', task});
                                    }}>
                              承認
                            </Button>
                          </>
                      )}


                      {nextAction === '出金確認' && (
                          <>
                            <Button className={'me-1'}
                                    variant={'outline-primary'}
                                    onClick={() => setModal({type: 'complete', task})}
                            >
                              承認登録
                            </Button>
                          </>
                      )}

                      {nextAction === '完了' && (
                          <Button className={'me-1'}
                                  variant={'outline-primary'}
                                  onClick={() => downloadPlazai(task.id)}
                          >
                            仕訳
                          </Button>
                      )}

                      <Button className={'me-1'}
                              variant={'outline-secondary'}
                              onClick={() => Navigate(`./${task.id}`)}
                      >
                        詳細
                      </Button>
                    </td>
                  </tr>
              ))}
              </tbody>
            </Table>
          </div>


          <div className={'d-flex justify-content-end'}>
            <Pager activePage={page}
                   pageSize={pageSize}
                   totalCount={result.total}
                   displaySize={2}
                   onChange={({page}) => handlePage(page)}
            />
          </div>
        </Container>


        <ConfirmModal show={modal !== false && modal.type === 'download'}
                      size={'lg'}
                      confirmLabel={'ダウンロードする'}
                      confirmButtonProps={{
                        variant: 'primary',
                      }}
                      onConfirm={downloadFile}
                      onCancel={() => setModal(false)}>

          <div className={'d-flex flex-column'}>
            <div className={'d-flex justify-content-start align-items-center'}>
              <label style={{width: '5rem'}}>振込日</label>
              <FormText value={date}
                        type={'date'}
                        onChange={event => setDate(event.currentTarget.value)}/>
            </div>
          </div>
        </ConfirmModal>


        <ConfirmModal show={modal !== false && modal?.type === 'register'}
                      confirmLabel={'承認する'}
                      confirmButtonProps={{
                        variant: 'primary',
                      }}
                      keyword={'総合振込確認'}
                      onConfirm={register}
                      onCancel={() => setModal(false)}>

          総合振込の内容を承認します。
        </ConfirmModal>


        <ConfirmModal show={modal !== false && modal?.type === 'complete'}
                      confirmLabel={'承認する'}
                      confirmButtonProps={{
                        variant: 'primary',
                      }}
                      keyword={'出金確認'}
                      onConfirm={complete}
                      onCancel={() => setModal(false)}>

          出金処理を承認します。
        </ConfirmModal>
      </>
  );
}
